/**
 * My day (3.2–3.3): one clinician-day laid out on a vertical time axis.
 *
 * A week column is an agenda — a sorted list with no time in it — so a hole in
 * the day is invisible and "later" has nowhere to be dragged to. This is the
 * same day, with the time axis put back: stops sized by their planned duration
 * and the drive between them drawn from the visit resources' own drive minutes,
 * in the same column as the stops, so it reads as travel from one to the next.
 *
 * **An open span is drawn as an open span and nothing else.** The axis used to
 * label every hole of half an hour or more ("Open 12:45–14:15") inside a dotted
 * band; Pete took the labels off on 2026-09-18 — *"I don't want to see explicit
 * open slots on the day view. The empty space is clue enough."* The hole is the
 * point of the view and the blank stretch between two stops is what shows it.
 *
 * Pure, and built from what My week already reads: the cards come from
 * `selectMyWeek` (so a card reads the same in either view and the footer numbers
 * cannot disagree with the week column's) and the drive minutes come from the
 * same effective calendar the cards were built from.
 *
 * Three rules the rest of the view depends on:
 *
 * - **Only a card with a genuine window sits on the axis.** A placeholder that
 *   has never been given a time carries no `windowStart`; My week draws it at a
 *   fallback hour so the column has something to sort by, and drawing that hour
 *   on a time axis would put an assessment-length block straight through the one
 *   gap this view exists to show. Those cards go to the **Unscheduled** tray,
 *   off the grid. A hold has a real window and stays on the axis.
 * - **Drive belongs to the stop it arrives at.** That is the product's own model
 *   (`services/updateDriveTimes.ts`): `startOfShiftDriveTime` on the first stop of
 *   the shift, `interVisitDriveTime` on every later one, and
 *   `endOfShiftDriveTime` on the last — the drive home.
 * - **A stop's drive is part of the stop's footprint, not a second thing beside
 *   it.** The product's own calendar carries drive as Mobiscroll's `bufferBefore`
 *   and `bufferAfter` on the event itself (`apps/web/src/utils/eventsHelper.ts`),
 *   so the scheduler's ordinary side-by-side layout applies to a drive that
 *   collides with something exactly as it applies to two colliding visits. My day
 *   does the same: **lanes are assigned over footprints**, so a drive longer than
 *   the hole before its stop is drawn in a lane beside whatever it runs into
 *   rather than on top of it (Pete, 2026-09-18: *"I'd suggest including drive
 *   time in the side by side treatment. That's how we handle it elsewhere in the
 *   product."*). A stop can never be laid out as colliding with its own drive,
 *   because the lane algorithm sees one interval per stop and the card and its
 *   band divide that one interval between them.
 * - **Overlapping spans count once.** The seed's start-of-care hold sits over
 *   routine visits, and two things sharing an hour occupy one stretch of the
 *   axis, not two — which is what the axis bounds are measured over.
 */
import type { DateKey, DemoState } from "@caredna-marlin/utils/homeHealthDemo/state";
import { formatInBranchZone, selectDemoNow, toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { selectEffectiveCalendar } from "./effectiveCalendar";
import { selectMyWeek, type WeekCard } from "./myWeek";
import { atWall, type TimeSentence } from "@caredna-marlin/utils/homeHealthDemo/engine/timeText";

/**
 * What a dragged stop snaps to. Five minutes, not fifteen (Pete, 2026-09-18):
 * a clinician re-times a day in the units she says out loud, and the hour lines
 * the axis draws are unchanged — this is the drop grid, not the ruler.
 */
export const SNAP_MINUTES = 5;
/**
 * The shortest a stop is ever *given* — the floor on a card's own span, and on
 * the end time a drop writes to the server. The snap grid used to double as this
 * floor; at five minutes a card with no end time would be six pixels tall, so the
 * floor is its own number and stays at the old quarter hour.
 */
export const MIN_STOP_MINUTES = 15;
/**
 * The shortest a stop is ever **drawn**, which is not the same number. A card
 * needs its time line and its title, and at the axis's 1.2px a minute that costs
 * about 50px: 2px of border, 8px of padding, a ~20px caption line and a ~20px
 * body line. Forty-two minutes would just cover it; this is 45, which leaves four
 * pixels of slack against sub-pixel rounding and is exactly a routine visit's
 * length, so no routine visit is ever stretched by it. A shorter stop overhangs
 * its true end rather than cutting its title off mid-word (Pete, 2026-09-18) —
 * and the overhang is in the footprint the lanes are assigned over, so a stop
 * close behind a short one gets a lane beside it rather than being drawn through
 * the overhang.
 */
export const MIN_DRAWN_MINUTES = 45;
/** Breathing room above the first hour of the pattern and below the last. */
export const AXIS_MARGIN_MINUTES = 30;
/** Fallback day when the clinician has no shift on the date (a day off opened from the week). */
export const FALLBACK_DAY = { start: "08:00", end: "17:00" };

export function minutesOfHhmm(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

export function hhmmOfMinutes(minutes: number): string {
  const clamped = Math.max(0, Math.min(24 * 60 - 1, Math.round(minutes)));
  return `${String(Math.floor(clamped / 60)).padStart(2, "0")}:${String(clamped % 60).padStart(2, "0")}`;
}

export function snapToGrid(minutes: number, grid: number = SNAP_MINUTES): number {
  return Math.round(minutes / grid) * grid;
}

/** A stop on the axis: one card, its span in minutes from midnight, and the lane it was given. */
export interface DayStop {
  card: WeekCard;
  /** Minutes from midnight, branch-zone wall clock. */
  start: number;
  end: number;
  /**
   * Where the card's box actually stops, `end` floored to `MIN_DRAWN_MINUTES`. A
   * short stop overhangs its true end here so its title has somewhere to be; what
   * a drop writes to the server is still measured off `end`.
   */
  drawnEnd: number;
  /** Drive arriving at this stop, minutes. Zero for anything but a real visit. */
  driveBefore: number;
  /** Drive leaving this stop — the drive home, on the last visit of the day only. */
  driveAfter: number;
  /** 0-based column within the cluster of stops this one shares time with. */
  lane: number;
  /** How many lanes that cluster needs; 1 when nothing overlaps. */
  laneCount: number;
}

/**
 * Everything one stop takes up on the axis: its drive in, the card as it is
 * drawn, and the drive home if it carries one. This is the interval the lanes are
 * assigned over and the axis bounds are measured across — one interval per stop,
 * which is why a stop and its own drive can never be laid out as colliding.
 */
export function stopFootprint(stop: Pick<DayStop, "start" | "drawnEnd" | "driveBefore" | "driveAfter">): { start: number; end: number } {
  return { start: stop.start - stop.driveBefore, end: stop.drawnEnd + stop.driveAfter };
}

/**
 * A drive segment as it is drawn: hatched, **in the lane column with the stops**
 * rather than in a rail beside them, running from the stop above to the stop
 * below, so travel reads as part of the day's flow and not as marginalia (Pete,
 * 2026-09-18). It is drawn at the full width of its stop's lane, because it is
 * part of that stop's footprint; it carries the stop's lane for exactly that
 * reason, and a drive that runs into something else is beside it rather than
 * over it.
 *
 * While a move either side of it is still optimistic the segment is `stale` —
 * dashed, with no minutes and no length claim, because the overlay carries the
 * visit's *old* drive until the poll lands.
 */
export interface DriveSegment {
  key: string;
  start: number;
  end: number;
  minutes: number;
  stale: boolean;
  /** The lane of the stop this drive arrives at, and how many lanes that cluster needs. */
  lane: number;
  laneCount: number;
}

export interface MyDayView {
  date: DateKey;
  weekdayLabel: string;
  dateLabel: string;
  isToday: boolean;
  isPast: boolean;
  /** Axis bounds in minutes from midnight, on the hour. */
  axisStart: number;
  axisEnd: number;
  stops: DayStop[];
  drives: DriveSegment[];
  /** Cards with no genuine window: drawn above the axis, off the time grid. */
  tray: WeekCard[];
  /** The day's points and drive minutes — the same numbers as the week column's footer. */
  points: number;
  driveMinutes: number;
  /** The widest cluster on the day: 1 when nothing on it overlaps. */
  laneCount: number;
}

/** Everything that occupies time on the axis. A tray card occupies nothing. */
function isOnAxis(card: WeekCard): boolean {
  return card.onAxis;
}

/**
 * What counts as somebody already being there. A hold cedes its slot to a real
 * visit, so it is not in this set — landing on one is the hold being taken up.
 * Since 2026-09-19 this decides what a drop is *told about*, not what it is
 * refused.
 */
export function occupiesSpace(card: WeekCard): boolean {
  return card.kind === "visit" || card.kind === "block";
}

function mergeSpans(spans: { start: number; end: number }[]): { start: number; end: number }[] {
  const sorted = [...spans].sort((a, b) => a.start - b.start || a.end - b.end);
  const out: { start: number; end: number }[] = [];
  for (const span of sorted) {
    const last = out[out.length - 1];
    if (last && span.start <= last.end) last.end = Math.max(last.end, span.end);
    else out.push({ ...span });
  }
  return out;
}

/**
 * Parallel lanes for anything that shares time, so nothing hides behind anything
 * else. Greedy by start: a stop takes the leftmost lane whose previous occupant
 * has finished. `laneCount` is per cluster — a two-lane morning does not narrow a
 * single-lane afternoon.
 *
 * It runs over **footprints**, not over card spans: a stop's drive in, and the
 * drive home it carries, share the lane the card is given, so a drive longer than
 * the hole before it puts its stop beside what it runs into instead of over it.
 * One interval per stop is also what keeps a stop from ever colliding with its
 * own drive.
 */
/**
 * How much a footprint may run into the one before it and still share its lane.
 * The seed pins a ten-minute turnaround against a twelve-minute drive, so three
 * of Dana's afternoon cards collide by **two minutes** and would otherwise all be
 * drawn at half width for 120 seconds of overlap — noise, not information. Strictly
 * less than, so a five-minute collision is still a real one and still splits.
 */
const LANE_OVERLAP_TOLERANCE_MINUTES = 5;

function assignLanes(stops: DayStop[]): void {
  const span = new Map<DayStop, { start: number; end: number }>(stops.map((s) => [s, stopFootprint(s)]));
  const order = [...stops].sort((a, b) => span.get(a)!.start - span.get(b)!.start || span.get(a)!.end - span.get(b)!.end);
  const laneEnds: number[] = [];
  let cluster: DayStop[] = [];
  let clusterEnd = -Infinity;
  const closeCluster = () => {
    if (!cluster.length) return;
    const count = Math.max(...cluster.map((s) => s.lane)) + 1;
    for (const stop of cluster) stop.laneCount = count;
    cluster = [];
    laneEnds.length = 0;
  };
  for (const stop of order) {
    const { start, end } = span.get(stop)!;
    if (start >= clusterEnd) {
      closeCluster();
      clusterEnd = -Infinity;
    }
    let lane = laneEnds.findIndex((laneEnd) => laneEnd < start + LANE_OVERLAP_TOLERANCE_MINUTES);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(end);
    } else {
      laneEnds[lane] = end;
    }
    stop.lane = lane;
    cluster.push(stop);
    clusterEnd = Math.max(clusterEnd, end);
  }
  closeCluster();
}

/** One clinician-day on a time axis. `handle` and `date` are the same two the week column carries. */
export function selectMyDay(state: DemoState, handle: string, date: DateKey, wallNow: Date = new Date()): MyDayView {
  const tz = state.config.branch.timeZone;
  const week = selectMyWeek(state, handle, date, wallNow);
  const day = week.days.find((d) => d.date === date);
  const providerId = state.meta.bindings.providerIdsByHandle[handle];

  // Drive belongs to the stop it arrives at, in the day's own order — the model
  // `services/updateDriveTimes.ts` writes.
  const driveBefore = new Map<number, number>();
  const driveAfter = new Map<number, number>();
  if (providerId !== undefined) {
    const { visits } = selectEffectiveCalendar(state, providerId, { start: date, end: date });
    visits.forEach((vr, i) => {
      driveBefore.set(vr.visitId, i === 0 ? vr.startOfShiftDriveMinutes : vr.interVisitDriveMinutes);
      if (i === visits.length - 1) driveAfter.set(vr.visitId, vr.endOfShiftDriveMinutes);
    });
  }

  const cards = day?.cards ?? [];
  const tray = cards.filter((c) => !isOnAxis(c));
  const stops: DayStop[] = cards.filter(isOnAxis).map((card) => {
    const start = minutesOfHhmm(card.start);
    const end = Math.max(start + MIN_STOP_MINUTES, minutesOfHhmm(card.end || card.start));
    return {
      card,
      start,
      end,
      drawnEnd: Math.max(end, start + MIN_DRAWN_MINUTES),
      driveBefore: card.realVisitId !== undefined ? (driveBefore.get(card.realVisitId) ?? 0) : 0,
      driveAfter: card.realVisitId !== undefined ? (driveAfter.get(card.realVisitId) ?? 0) : 0,
      lane: 0,
      laneCount: 1,
    };
  });
  stops.sort((a, b) => a.start - b.start || a.end - b.end);
  assignLanes(stops);

  // The visits in time order, so a pending move makes the segment on each side of
  // it stale rather than only its own. A **block** that is still in flight makes
  // every segment on the day stale: the server reprices the whole shift around a
  // block it can route (`services/calendarBlock.ts`), so until the poll lands no
  // leg of the day is a number this view is entitled to draw.
  const timeline = stops.filter((s) => s.card.kind === "visit");
  const blockPending = stops.some((s) => s.card.kind === "block" && s.card.pending);
  const drives: DriveSegment[] = [];
  timeline.forEach((stop, i) => {
    const previous = timeline[i - 1];
    const lanes = { lane: stop.lane, laneCount: stop.laneCount };
    if (stop.driveBefore > 0) {
      drives.push({
        key: `drive-in-${stop.card.id}`,
        start: stop.start - stop.driveBefore,
        end: stop.start,
        minutes: stop.driveBefore,
        stale: blockPending || stop.card.pending || !!previous?.card.pending,
        ...lanes,
      });
    }
    if (stop.driveAfter > 0) {
      drives.push({ key: `drive-out-${stop.card.id}`, start: stop.end, end: stop.end + stop.driveAfter, minutes: stop.driveAfter, stale: blockPending || stop.card.pending, ...lanes });
    }
  });

  // The union of every footprint — what the axis is stretched to hold. Two things
  // that share an hour occupy it once, and a tray card occupies nothing at all.
  const occupied = mergeSpans(stops.map(stopFootprint));

  // The axis runs the clinician's pattern, with a margin, and stretches for
  // anything the day already carries outside it.
  const shifts = providerId === undefined ? [] : state.server.shifts.filter((s) => s.providerId === providerId && s.status === "confirmed" && toDateKey(new Date(s.startIso), tz) === date);
  const shiftStart = shifts.length ? Math.min(...shifts.map((s) => minutesOfHhmm(formatInBranchZone(new Date(s.startIso), tz, "HH:mm")))) : minutesOfHhmm(FALLBACK_DAY.start);
  const shiftEnd = shifts.length ? Math.max(...shifts.map((s) => minutesOfHhmm(formatInBranchZone(new Date(s.endIso), tz, "HH:mm")))) : minutesOfHhmm(FALLBACK_DAY.end);
  const earliest = Math.min(shiftStart, ...occupied.map((s) => s.start));
  const latest = Math.max(shiftEnd, ...occupied.map((s) => s.end));
  const axisStart = Math.max(0, Math.floor((earliest - AXIS_MARGIN_MINUTES) / 60) * 60);
  const axisEnd = Math.min(24 * 60, Math.ceil((latest + AXIS_MARGIN_MINUTES) / 60) * 60);

  return {
    date,
    weekdayLabel: day?.weekdayLabel ?? formatInBranchZone(new Date(`${date}T12:00:00Z`), "UTC", "EEE"),
    dateLabel: day?.dateLabel ?? formatInBranchZone(new Date(`${date}T12:00:00Z`), "UTC", "MMM d"),
    isToday: day?.isToday ?? date === toDateKey(selectDemoNow(state, wallNow), tz),
    isPast: day?.isPast ?? date < toDateKey(selectDemoNow(state, wallNow), tz),
    axisStart,
    axisEnd,
    stops,
    drives,
    tray,
    points: day?.points ?? 0,
    driveMinutes: day?.driveMinutes ?? 0,
    laneCount: stops.reduce((max, s) => Math.max(max, s.laneCount), 1),
  };
}

/**
 * What a span would land across. **Nothing is refused** (Pete, 2026-09-19: *"I
 * want to allow dropping visits into conflict with other blocks/visits. It makes
 * it hard to rearrange things otherwise"*): until then a drop onto another stop's
 * span failed silently, which meant a day could not be shuffled — A cannot go past
 * B with nowhere to put A down — and it was the one place this demo's guardrails
 * blocked rather than informed.
 *
 * So this answers with the stops, for the strip to name, and the caller lands the
 * drop either way. A hold is not among them: it cedes its slot to a real visit, so
 * a stop landing on one is the hold being taken up rather than a double booking.
 *
 * Measured on the cards' own spans, not their footprints — an overlap is two
 * people expected in two places at once, which is about the visits and not about
 * the drive between them.
 */
export function stopsOverlapping(view: MyDayView, start: number, end: number, ignoreCardId?: string): DayStop[] {
  return view.stops.filter((s) => s.card.id !== ignoreCardId && occupiesSpace(s.card) && start < s.end && end > s.start);
}

/**
 * What the guardrail strip says about an overlap: the first stop by name and hour,
 * and a count of the rest. Undefined when the span lands clear, so the strip shows
 * nothing rather than reserving a line for good news.
 */
export function describeOverlap(stops: DayStop[]): TimeSentence | undefined {
  const [first, ...rest] = [...stops].sort((a, b) => a.start - b.start);
  if (!first) return undefined;
  // Minutes from midnight are the axis's own unit; `hhmmOfMinutes` turns them
  // back into the branch wall clock the view then phrases under the setting.
  return [`overlaps ${first.card.title} `, atWall(hhmmOfMinutes(first.start)), "–", atWall(hhmmOfMinutes(first.end)), rest.length === 0 ? "" : ` and ${rest.length} other${rest.length === 1 ? "" : "s"}`];
}

/** The hour lines the axis draws, top to bottom. */
export function hourLines(view: Pick<MyDayView, "axisStart" | "axisEnd">): number[] {
  const out: number[] = [];
  for (let m = view.axisStart; m <= view.axisEnd; m += 60) out.push(m);
  return out;
}
